import { TFunction } from 'i18next';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { APP_ROUTES } from '../../constants/appRoutes';

import instruction1 from '../../assets/instruction1.png';
import instruction2 from '../../assets/instruction2.png';
import instruction3 from '../../assets/instruction3.png';
import instruction4 from '../../assets/instruction4.png';
import instruction5 from '../../assets/instruction5.png';
import dotPaintingInstruction1 from '../../assets/dotPaintingInstruction1.png';
import dotPaintingInstruction2 from '../../assets/dotPaintingInstruction2.png';
import dotPaintingInstruction3 from '../../assets/dotPaintingInstruction3.png';
import dotPaintingInstruction4 from '../../assets/dotPaintingInstruction4.png';
import dotPaintingInstruction5 from '../../assets/dotPaintingInstruction5.png';
import embroideryInstruction1 from '../../assets/embroideryInstruction1.png';
import embroideryInstruction2 from '../../assets/embroideryInstruction2.png';
import embroideryInstruction3 from '../../assets/embroideryInstruction3.png';
import embroideryInstruction4 from '../../assets/embroideryInstruction4.png';
import embroideryInstruction5 from '../../assets/embroideryInstruction5.png';
import { isObject } from '../../lib/shared';

import styles from './styles.module.scss';
import { PhotoContext } from '../../context/PhotoProvider';
import { ProductTypes } from '../../types/enums';
import NavButton from '../../components/NavButton';

const getDataByTypeMap = (t: TFunction) => ({
  [ProductTypes.MOSAIC]: {
    blocks: [
      {
        src: instruction1,
        text: 'The instruction includes sector numbers, just like on the mosaic base. You can switch between them by selecting the desired one.',
      },
      {
        src: instruction2,
        text: 'The numbers indicate how many rhinestones of the same color will go consecutively in a row.',
      },
      {
        src: instruction3,
        text: 'For convenience, you can select one or several colors, and they will be highlighted on the scheme. If cells are marked with an “X,” they do not need to be filled.',
      },
      {
        src: instruction4,
        text: (
          <>
            {t('You can mark already placed rhinestones by clicking on them:')}
            <br />
            {t('1 click - marks one cell,')} <br />
            {t('2 clicks - highlights the entire row up to the desired cell.')}
          </>
        ),
      },
      {
        src: instruction5,
        text: 'To remove a mark, click on the desired rhinestone again or use the button below the diagram.',
      },
    ],
    backRoute: APP_ROUTES.INTERACTIVE_INSTRUCTION,
  },
  [ProductTypes.EMBROIDERY]: {
    blocks: [
      {
        src: embroideryInstruction1,
        text: 'The scheme is divided into 10x10 cell sectors, which can be switched by selecting the desired one.',
      },
      {
        src: embroideryInstruction2,
        text: 'The numbers indicate how many rhinestones of the same color will go consecutively in a row.',
      },
      {
        src: embroideryInstruction3,
        text: 'For convenience, you can select one or several colors, and they will be highlighted on the scheme. If cells are marked with an “X,” they do not need to be filled.',
      },
      {
        src: embroideryInstruction4,
        text: (
          <>
            {t('You can mark already embroidered crosses by clicking on them:')}{' '}
            <br />
            {t('1 click - marks a single cell,')}
            <br />
            {t('2 clicks - highlights the entire row up to the desired cell.')}
          </>
        ),
      },
      {
        src: embroideryInstruction5,
        text: 'To remove a mark, click on the desired rhinestone again or use the button below the diagram.',
      },
    ],
    backRoute: APP_ROUTES.EMBROIDERY_INTERACTIVE_INSTRUCTION,
  },
  [ProductTypes.DOT_PAINTING]: {
    blocks: [
      {
        src: dotPaintingInstruction1,
        text: 'dotPaintingInstruction1',
      },
      {
        src: dotPaintingInstruction2,
        text: 'dotPaintingInstruction2',
      },
      {
        src: dotPaintingInstruction3,
        text: 'dotPaintingInstruction3',
      },
      {
        src: dotPaintingInstruction4,
        text: 'youCanMarkDotPainting',
      },
      {
        src: dotPaintingInstruction5,
        text: 'dotPaintingInstruction5',
      },
    ],
    backRoute: APP_ROUTES.DOT_PAINTING_INTERACTIVE_INSTRUCTION,
  },
  [ProductTypes.PAINTING]: {
    blocks: [
      {
        src: instruction1,
        text: 'theInstrutionHasSectors',
      },
      {
        src: instruction2,
        text: 'theNumbersShow',
      },
      {
        src: instruction3,
        text: 'forConvinience',
      },
      {
        src: instruction4,
        text: 'youCanMark',
      },
      {
        src: instruction5,
        text: 'toRemoveTheMark',
      },
    ],
    backRoute: APP_ROUTES.INTERACTIVE_INSTRUCTION,
  },
});

const HowToUseInstruction = () => {
  const navigate = useNavigate();
  const { order, productType } = useContext(PhotoContext);
  const { t } = useTranslation();
  const dataByTypeMap = useMemo(() => getDataByTypeMap(t), [t]);

  const navigateBack = () => {
    navigate(dataByTypeMap[productType].backRoute + order);
  };

  return (
    <div className={styles.wrapper}>
      <h1>{t('How to use the instruction?')}</h1>
      {dataByTypeMap[productType].blocks.map((el, i) => (
        <div key={i} className={styles.instructionBlock}>
          <div className={styles[`inner_${i}`]}>
            <img src={el.src} alt="instruction-photo" />
          </div>
          <p className={styles.description}>
            {isObject(el.text) ? el.text : t(el.text as string)}
          </p>
        </div>
      ))}
      <p className={styles.toBuild}>
        {t(
          'For collaborative assembly, you can open the link on other devices.',
        )}
        <br /> {t('All marks are saved when the page is closed.')}
      </p>
      <NavButton type="back" onClick={navigateBack} text="Back to Assembly" />
    </div>
  );
};

export default HowToUseInstruction;
